import { baseApi } from "@/shared/api";
import type { IncomeTimeSeries } from "@/features/finance/types/api";
import type {
  IncomeRecord,
  IncomeRecordListItem,
  IncomeRecordCreateRequest,
  IncomeRecordUpdateRequest,
  AvailableExternalIncome,
  IncomeRecordSummary,
  AccountCodesResponse,
  IncomeRecordsListQueryParams,
} from "../types";

interface IncomeTimeSeriesParams {
  start_date: string;
  end_date: string;
  interval: string;
  status?: string;
  project_id?: number;
}

export const incomeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIncomeRecords: builder.query<IncomeRecordListItem[], IncomeRecordsListQueryParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          if (params.income_type) searchParams.append("income_type", params.income_type);
          if (params.status) searchParams.append("status", params.status);
          if (params.pool) searchParams.append("pool", params.pool);
          if (params.date_from) searchParams.append("date_from", params.date_from);
          if (params.date_to) searchParams.append("date_to", params.date_to);
          if (params.search) searchParams.append("search", params.search);
          if (params.ordering) searchParams.append("ordering", params.ordering);
        }
        const queryString = searchParams.toString();
        return {
          url: `/api/accounting/income-records/${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "IncomeRecords" as const, id })),
              { type: "IncomeRecords" as const, id: "LIST" },
            ]
          : [{ type: "IncomeRecords" as const, id: "LIST" }],
    }),

    getIncomeRecord: builder.query<IncomeRecord, number>({
      query: (id) => ({
        url: `/api/accounting/income-records/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "IncomeRecords" as const, id }],
    }),

    getIncomeSummary: builder.query<IncomeRecordSummary, IncomeRecordsListQueryParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          if (params.income_type) searchParams.append("income_type", params.income_type);
          if (params.status) searchParams.append("status", params.status);
          if (params.pool) searchParams.append("pool", params.pool);
          if (params.date_from) searchParams.append("date_from", params.date_from);
          if (params.date_to) searchParams.append("date_to", params.date_to);
        }
        const queryString = searchParams.toString();
        return {
          url: `/api/accounting/income-records/summary/${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "IncomeRecords" as const, id: "LIST" }],
    }),

    getAccountCodes: builder.query<AccountCodesResponse, void>({
      query: () => ({
        url: "/api/accounting/income-records/account-codes/",
        method: "GET",
      }),
      providesTags: [{ type: "IncomeRecords" as const, id: "ACCOUNT_CODES" }],
    }),

    getAvailableExternalIncomes: builder.query<AvailableExternalIncome[], void>({
      query: () => ({
        url: "/api/accounting/income-records/?pool=external&available=true",
        method: "GET",
      }),
      providesTags: [{ type: "IncomeRecords" as const, id: "LIST" }],
    }),

    createIncomeRecord: builder.mutation<IncomeRecord, IncomeRecordCreateRequest>({
      query: (body) => ({
        url: "/api/accounting/income-records/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "IncomeRecords" as const, id: "LIST" }, "PoolBalances"],
    }),

    updateIncomeRecord: builder.mutation<IncomeRecord, { id: number; data: IncomeRecordUpdateRequest }>({
      query: ({ id, data }) => ({
        url: `/api/accounting/income-records/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "IncomeRecords" as const, id },
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),

    deleteIncomeRecord: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/accounting/income-records/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "IncomeRecords" as const, id },
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),

    approveIncomeRecord: builder.mutation<IncomeRecord, number>({
      query: (id) => ({
        url: `/api/accounting/income-records/${id}/approve/`,
        method: "POST",
        credentials: "include" as const,
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(incomeApi.util.updateQueryData("getIncomeRecord", id, () => data));
        } catch {
          return;
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: "IncomeRecords" as const, id },
        { type: "IncomeRecords" as const, id: "LIST" },
        "PoolBalances",
        "Budget",
        { type: "Organization" as const, id: "ME" },
      ],
    }),

    getIncomeTimeSeries: builder.query<IncomeTimeSeries, IncomeTimeSeriesParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        searchParams.set("interval", params.interval);
        if (params.status) searchParams.set("status", params.status);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        return {
          url: `/api/accounting/income-records/time-series/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "IncomeRecords" as const, id: "TIME_SERIES" }],
    }),

  }),
});

export const {
  useGetIncomeRecordsQuery,
  useGetIncomeRecordQuery,
  useGetIncomeSummaryQuery,
  useGetAccountCodesQuery,
  useGetAvailableExternalIncomesQuery,
  useCreateIncomeRecordMutation,
  useUpdateIncomeRecordMutation,
  useDeleteIncomeRecordMutation,
  useApproveIncomeRecordMutation,
  useGetIncomeTimeSeriesQuery,
} = incomeApi;
